import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { AddressType, Country } from "../../profile/models/masterdata.model";
import { OPERATION, SupplierAddress } from "../../profile/models/supplier.model";
import { GeneralSettings } from "../../profile/models/generic.model";
import { OnboardingDocument } from "../../profile/models/onboarding.model";
import { isNullOrEmpty, isNullOrUndefined, validateAllFormFields } from "../../shared/utils/utils";
import { I18nService } from "../../shared/services/i18n.service";
import { DocumentsGridComponent } from "../documents/documents-grid/documents-grid.component";
import { SelectAutocompleteComponent } from "../autocomplete/select-autocomplete.component";
import { US_STATE_LIST } from 'app/shared/constants';
import { DecrementLoaderCount, IncrementLoaderCount } from "app/shared/store/actions";
import { DocumentTypeProps, QuestionFieldConfig, SupplierConfigurationCategoryEnum, SupplierConfigurationPropertiesPayload } from "app/profile/models/tax.model";
import { SupplierState } from "app/profile/store/state/supplierstate.state";
import { Store, select } from "@ngrx/store";
import { TaxService } from "app/profile/services/tax.service";
import { GetCountryList } from "app/profile/store/app.actions";
import { SelectCountries } from "app/profile/store/app.selector";

@Component({
    selector: 'address-edit',
    templateUrl: './address-edit.component.html',
    styleUrls: ['./address-edit.component.scss']
})
export class AddressEditComponent implements OnInit, OnChanges, AfterViewInit {

    addressForm: FormGroup;
    purposeControl: FormControl;
    addressLine1Control: FormControl;
    addressLine2Control: FormControl;
    cityControl: FormControl;
    stateControl: FormControl;
    countryControl: FormControl;
    zipCodeControl: FormControl;
    commentsControl: FormControl;
    documentAdding: boolean = false;
    additionalMandatoryDocs: DocumentTypeProps[] = [];
    countryList: Country[];

    /**
     * Address values from the parent component
     */
    @Input('tsl-initialValue') supplierAddressValues: SupplierAddress;

    @Input('tsl-isReactivation') isReactivation: boolean = false;

    /** Determines if the controls should be disabled */
    @Input('tsl-readOnly') readOnly = false;

    /** Required supplier code to be provided by onboaridng parent component */
    @Input('tsl-supplierCode') supplierCode: string = '';

    /** Sets the address type id of the purpose dropdown and hides the dropdown */
    @Input('tsl-defaultAddressTypeName') defaultAddressTypeName: string = null;

    /** Determines whether to show the comments controls or not  */
    @Input('tsl-showCommentsControl') showCommentsControl: boolean = true;

    /** Determines if we hide the save button, so that the continue observable is the one that will trigger the submit action */
    @Input('tsl-hideSaveButton') hideSaveBtn: boolean = false;

    /** Label to display at the save button. Defaults to 'Next' */
    @Input('tsl-buttonLabel') saveButtonLabel: string = "Next";

    /** Collection of supported countries */
    @Input('tsl-countries') countries: Country[] = [];

    /** Collection of address types to display in 'purpose' dropdown */
    @Input('tsl-addressTypes') addressTypes: AddressType[] = [];

    /** Used to determine if the document section should be displayed or not */
    @Input('tsl-hideContactDocumentSection') hideContactDocumentSection: boolean = false;

    /** Address document data from document step status */
    @Input('tsl-requiredDocuments') requiredDocuments: OnboardingDocument[];

    /** General settings of the application */
    @Input('tsl-generalSettings') settings: GeneralSettings;

    /** Event to trigger when the form is submitted with valid data */
    @Output('tsl-onSave') onSubmitEvent: EventEmitter<{ supplierAddress: SupplierAddress }> =
        new EventEmitter<{ supplierAddress: SupplierAddress }>();

    /** Event to trigger when user submits a form with errors */
    @Output('tsl-onSaveError') onErrorEvent: EventEmitter<{ errors: ValidationErrors[], errorMessage: string }> =
        new EventEmitter<{ errors: ValidationErrors[], errorMessage: string }>();

    /** Reference to the documents grid in the template */
    @ViewChild('addressDocumentsGrid', { static: false }) documentsGrid: DocumentsGridComponent;

    /** Reference to the country select-autocomplete component */
    @ViewChild('countryAutocomplete', { static: false }) countryAutocomplete: SelectAutocompleteComponent;

    constructor(
        private translate: I18nService,
        private store: Store<SupplierState>,
        private taxService: TaxService
    ) {
        //  Instanciate control objects with validadors
        this.purposeControl = new FormControl(null, [Validators.required]);
        this.addressLine1Control = new FormControl('', [Validators.required, Validators.maxLength(50)]);
        this.addressLine2Control = new FormControl('', [Validators.maxLength(50)]);
        this.cityControl = new FormControl('', [Validators.required, Validators.maxLength(30)]);
        this.stateControl = new FormControl('', [Validators.required, Validators.maxLength(30)]);
        this.countryControl = new FormControl('', []);
        this.zipCodeControl = new FormControl('', [Validators.required, Validators.maxLength(10)]);
        this.commentsControl = new FormControl('', [Validators.maxLength(100)]);

        //  Set form group
        this.addressForm = new FormGroup({
            purpose: this.purposeControl,
            addressLine1: this.addressLine1Control,
            addressLine2: this.addressLine2Control,
            city: this.cityControl,
            state: this.stateControl,
            country: this.countryControl,
            zipCode: this.zipCodeControl,
            comments: this.commentsControl
        }, [this.USStateValidator(), this.USZipValidator()]);

        this.store.dispatch(new GetCountryList());

        this.countryControl.setValidators([Validators.required, Validators.maxLength(30), this.CountryValidator()])
        this.countryControl?.valueChanges.subscribe(countryValue => {
            this.CallAdditionalConfigurationService(this.supplierCode, null, countryValue, [] , false , false);
        });


        //  Initialize values of the form if any initial value was provided
        this.setControlValues();


    }

    ngOnInit(): void {
        //  Initialize tax form
        this.store.pipe(select(SelectCountries)).subscribe(res => {
            if (res) {
                this.countryList = res;
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.supplierAddressValues) {
            //  Reset the control values when the input changes
            this.setControlValues();
        }

        if (changes.readOnly) {
            //  Enable/Disable controls
            //  Required to use setTimeout due to issue when disabling controls in ngInit
            //  See for more: github.com/angular/angular/issues/22556
            setTimeout(() => this.enableControls(), 500);
        }
    }

    /** Updates the control values of the address form to have the values from the supplierAddressValues input property */
    private setControlValues() {
        if (this.supplierAddressValues) {
            this.addressLine1Control.setValue(this.supplierAddressValues.Street1, { emitEvent: false });
            this.addressLine2Control.setValue(this.supplierAddressValues.Street2, { emitEvent: false });
            this.cityControl.setValue(this.supplierAddressValues.City, { emitEvent: false });
            this.stateControl.setValue(this.supplierAddressValues.State, { emitEvent: false });
            this.countryControl.setValue(this.supplierAddressValues.CountryCode, { emitEvent: false });
            this.countryAutocomplete?.setValue(this.supplierAddressValues.CountryCode);
            this.zipCodeControl.setValue(this.supplierAddressValues.ZipCode, { emitEvent: false });
            this.commentsControl.setValue(this.supplierAddressValues.Comment, { emitEvent: false });
            if(this.supplierAddressValues?.CountryCode){
                this.countryAutocomplete?.setValue(this.supplierAddressValues.CountryCode, this.countries.filter(x => x.abbreviation == this.supplierAddressValues.CountryCode)[0]?.name);
            }

            this.CallAdditionalConfigurationService(this.supplierCode , null , this.supplierAddressValues.CountryCode , [] , false , false);
            if(this.supplierAddressValues?.DocumentInfos){

                // Additional check to not override required document field if no uploaded documents
                if(this.supplierAddressValues?.DocumentInfos?.length > 0)
                {
                    this.requiredDocuments = this.supplierAddressValues.DocumentInfos;
                    console.log(this.requiredDocuments);
                }
            }
        }

        if (this.defaultAddressTypeName) {
            this.purposeControl.setValue(this.defaultAddressTypeName, { emitEvent: false });
        } else if (this.supplierAddressValues && this.supplierAddressValues.Types && this.supplierAddressValues.Types.length > 0) {
            this.purposeControl.setValue(this.supplierAddressValues.Types[0], { emitEvent: false });
        }
        
    }

    /** Enables or disables address controls */
    private enableControls() {

        if (this.readOnly) {
            this.addressForm.disable({ emitEvent: false });
        } else {
            this.addressForm.enable({ emitEvent: false });
        }
    }

    /**
     * Form handler for the submit event
     */
    submit() {
        if (this.addressForm.valid) {

            //  Check if user has provided documents if required
            if (this.requiredDocuments?.length > 0) {
                this.documentsGrid.isValid().subscribe((val) => {
                    if (val === true) {
                        this.processSubmit()
                    } else {
                        //  Get form errors and trigger event
                        let errors = this.getFormValidationErrors();
                        this.onErrorEvent.emit({ errors, errorMessage: 'Address document is missing' });
                        return
                    }
                });
            }
            else {
                this.processSubmit();
            }

        } else {
            //  Get form errors and trigger event
            let errors = this.getFormValidationErrors();
            this.onErrorEvent.emit({ errors, errorMessage: 'Address tab is not completed' });
            validateAllFormFields(this.addressForm);

            if (this.documentsGrid) {
                //  Trigger validation to show error messages
                this.documentsGrid.isValid();
            }
        }
    }

    private processSubmit(){
            //  Trigger onSubmit event
            let address = new SupplierAddress();
            address.Street1 = this.addressLine1Control.value;
            address.Street2 = this.addressLine2Control.value;
            address.City = this.cityControl.value;
            address.State = this.stateControl.value;
            address.ZipCode = this.zipCodeControl.value;
            address.CountryCode = this.countryAutocomplete?.selectedValue || this.countryControl.value;
            address.Operation = OPERATION.CREATE;
            address.Types = [this.purposeControl.value];
            address.Comment = this.commentsControl.value;

            //  Append uploaded documents to address object
            let adrDocuments = this.documentsGrid && this.documentsGrid.documents && this.documentsGrid.documents?.filter(x => x.FileName != undefined && x.FileName != null && x.FileName.length > 0)?.length > 0 ? this.documentsGrid.documents.filter(x => x.FileName != undefined && x.FileName != null && x.FileName.length > 0).map(doc => ({ ...doc, FormGroup: null })) : [];
            address.DocumentInfos = adrDocuments;
            address.Documents = adrDocuments.map(doc => doc.DocumentID);
            address.StageID = this.supplierAddressValues?.StageID || null;

            this.onSubmitEvent.emit({ supplierAddress: address });
    }

    private getFormValidationErrors(): ValidationErrors[] {
        //  Create result array
        let result: ValidationErrors[] = [];
        Object.keys(this.addressForm.controls).forEach(key => {
            const controlErrors: ValidationErrors = this.addressForm.get(key).errors;
            if (controlErrors != null) {
                result.push(controlErrors);
            }
        });

        //  Return result with collection of validation errors
        return result;
    }

    /** Custom validator for US state that required only two capital letters */
    USStateValidator(): ValidatorFn {
        const USStateErrorName = 'USState';

        return (): { [key: string]: any } | null => {
            if (this.countryAutocomplete?.selectedValue == 'US') {

                //  Determine if state control has two capital letters
                const stateAbbreviations = US_STATE_LIST;
                let stateValue = this.stateControl?.value as string;
                let isValid = stateAbbreviations.includes(stateValue);

                if (!isValid) {

                    //  Add USState error to the state control
                    this.stateControl?.setErrors({ USState: { value: stateValue } });
                } else if (this.stateControl?.hasError(USStateErrorName)) {

                    //  Remove USState error from state control
                    delete this.stateControl.errors[USStateErrorName];
                    this.stateControl.updateValueAndValidity();
                }

                return isValid ? null : { [USStateErrorName]: { value: stateValue } };
            }

            //  Make sure the state control doesn't have the USState error
            if (this.stateControl?.hasError(USStateErrorName)) {

                //  Remove USState error from state control
                delete this.stateControl.errors[USStateErrorName];
                this.stateControl.updateValueAndValidity();
            }

            //  We ignore validation since country is not equal to US
            return null;
        };
    }

    /** Custom validator for US Zip that required only 5 digits */
    USZipValidator(): ValidatorFn {
        const USZipErrorName = 'USZip';

        return (): { [key: string]: any } | null => {
            if (this.countryAutocomplete?.selectedValue == 'US') {

                //  Determine if zipcode control has two capital letters
                let zipRegex = /^[0-9]{5}$/;
                let zipValue = this.zipCodeControl?.value as string;
                let isValid = zipValue?.match(zipRegex);

                if (!isValid) {

                    //  Add USZip error to the zipcode control
                    this.zipCodeControl?.setErrors({ USZip: { value: zipValue } });
                } else if (this.zipCodeControl?.hasError(USZipErrorName)) {

                    //  Remove USZip error from zipcode control
                    delete this.zipCodeControl.errors[USZipErrorName];
                    this.zipCodeControl.updateValueAndValidity();
                }

                return isValid ? null : { [USZipErrorName]: { value: zipValue } };
            }

            //  Make sure the zipcode control doesn't have the USZip error
            if (this.zipCodeControl?.hasError(USZipErrorName)) {

                //  Remove USZip error from state control
                delete this.zipCodeControl.errors[USZipErrorName];
                this.zipCodeControl.updateValueAndValidity();
            }

            //  We ignore validation since country is not equal to US
            return null;
        };
    }

    CountryValidator(): ValidatorFn {
        const CountryError = 'CountrySelected';

        return (): { [key: string]: any } | null => {
            if (this.countryControl?.value && this.countryAutocomplete) {
                let countryValue = this.countryControl?.value as string;
                let selectedValue = this.countryAutocomplete.selectedValue
                let countrySelected = this.countries.find(x => x.abbreviation == selectedValue);
                let isValid = countrySelected?.name == countryValue;
                if (isValid) {
                    if (this.countryControl.hasError(CountryError)) {
                        //  Remove Country error from Country control
                        delete this.countryControl.errors[CountryError];
                        this.countryControl.updateValueAndValidity();
                    }
                    return null
                } else {
                    //  Add USState error to the state control
                    this.countryControl?.setErrors({ CountrySelected: true });
                    return { [CountryError]: true };
                }
            }

            return null;
        };
    }

    //#region Select-Autocomplete Utilities
    /**
     * We are required to wrap the validator from the autocomplete component because the
     * ViewChild reference is not always populated, which will cause an error if we add the
     * autocomplete validator directly to the form control.
     * In this case, we use a wrapper direclty to the form control that checks if the autocomplete
     * refence is not null.
     * @returns functions that invokes the validator from autocomplete component
     */
    countryValidatorWrapper() {
        return (control: AbstractControl) => {
            if (!this.countryAutocomplete)
                return null;

            return this.countryAutocomplete.enteredOptionValidator()(control);
        }
    }
    //#region

    ngAfterViewInit(): void {
      this.countryAutocomplete?.setValue(this.supplierAddressValues?.CountryCode);
    }

    CallAdditionalConfigurationService(supplierCode: string, entityCode: string, country: string, additionalFilter: QuestionFieldConfig[], clearValues: boolean, clearFederalTaxIdValue: boolean = false) {
        if(isNullOrUndefined(country) || isNullOrEmpty(country))
        {
            return;
        }

        var countryCode = this.countryList?.filter(x => x.name == country)[0]?.abbreviation;

        if(isNullOrUndefined(countryCode) || isNullOrEmpty(countryCode))
        {
            if(this.countryList?.some(x => x.abbreviation == country) == false)
            {
                return;
            }
            else
            {
                countryCode = country;
            }
        }

        var configPropertiesVal = new SupplierConfigurationPropertiesPayload();
        configPropertiesVal.SupplierCode = supplierCode;
        configPropertiesVal.Country = countryCode;
        configPropertiesVal.Category = SupplierConfigurationCategoryEnum.ADDRESS;
        configPropertiesVal.Entity = entityCode;
        configPropertiesVal.AdditionalFilter = additionalFilter;

        this.store.dispatch(new IncrementLoaderCount());
        this.taxService.getAdditionalSupplierConfig(configPropertiesVal).subscribe(res => {

            // When the config is refresh than we should remove the existing addtional doc
            this.additionalMandatoryDocs?.forEach(additionalDoc => {
                if(this.requiredDocuments?.some(x => x.DocumentType == additionalDoc.name))
                {
                    this.requiredDocuments = this.requiredDocuments.filter(x => x.DocumentType != additionalDoc.name);
                }
            });
            
            // Add the documents list to the
            if (res.documentTypes?.length > 0) {
                this.requiredDocuments = this.requiredDocuments?.length > 0 ? this.requiredDocuments : [];

                if(res.documentTypes.find(x => x.name == "PFOADR")){
                    let adr = this.requiredDocuments.find(x => x.DocumentType == "ADR");
                    if(adr){
                        this.requiredDocuments = this.requiredDocuments.filter(x => x.DocumentType != adr.DocumentType);
                    }
                }

                res.documentTypes?.forEach(docType => {
                    var doc: OnboardingDocument = {
                        DocumentType: docType.name,
                        DocumentTypeDescription: docType.description,
                        Mode: "upload",
                        SignDateRequired: false,
                        SignedDate: null,
                        TypeID: docType.documentTypeID.toString(),
                        IsDocumentAvailableForSupplier : docType.isDocumentAvailableForSupplier,
                        AssociatedCategoryLinkId : docType.associatedCategoryLinkId,
                        IsOptional : docType.isOptional
                    };

                    if(!isNullOrUndefined(this.supplierAddressValues) && !isNullOrEmpty(this.supplierAddressValues) && this.supplierAddressValues?.DocumentInfos?.length > 0)
                    {
                        if(this.supplierAddressValues?.DocumentInfos?.some(addressDoc => addressDoc.DocumentType == docType.name))
                        {
                            doc = this.supplierAddressValues?.DocumentInfos?.filter(addressDoc => addressDoc.DocumentType == docType.name)[0];
                        }else{
                            this.requiredDocuments?.push(doc);
                        }
                    }else{
                        this.requiredDocuments?.push(doc);
                    }
                });

                this.additionalMandatoryDocs = res?.documentTypes;

            }

            this.documentsGrid?.setDocumentsFormArray();
            this.store.dispatch(new DecrementLoaderCount());
        });
    }
}
